import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link } from 'react-router-dom';
import { fetchAllDataAPI, addFormData } from '../utils/api';
import { toast } from 'react-toastify';
import { FaEdit, FaFilter, FaPlus, FaRedo, FaWhatsapp } from 'react-icons/fa';
import { CiExport } from 'react-icons/ci';
import CsvDownloader from 'react-csv-downloader';
import withAuth from '../utils/withAuth';
import moment from 'moment';

const SkeletonRow = () => (
    <tr>
        {[...Array(11)].map((_, i) => (
            <td key={i}>
                <div
                    style={{
                        height: 14,
                        borderRadius: 6,
                        background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite'
                    }}
                />
            </td>
        ))}
    </tr>
);

const WhatsappLeads = () => {
    const ref = useRef();

    const [leads, setLeads] = useState([]);
    const [counts, setCounts] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [historyModal, setHistoryModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [remarksModal, setRemarksModal] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [interactionHistory, setInteractionHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [currentPage, setPage] = useState(1);

    const perPage = 100;
    const npage = Math.ceil(leads.length / perPage);
    const records = leads.slice((currentPage - 1) * perPage, currentPage * perPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);

    useEffect(() => {
        loadLeads();
        loadCounts();
    }, []);

    const loadLeads = () => {
        setLoading(true);
        fetchAllDataAPI('sub-agent-whatsapp-leads')
            .then((res) => {
                if (res?.data?.status === 1) {
                    setLeads(res.data.leads || []);
                    setPage(1);
                } else {
                    setLeads([]);
                }
            })
            .catch(() => toast.error('Failed to load leads'))
            .finally(() => setLoading(false));
    };

    const loadCounts = () => {
        fetchAllDataAPI('sub-agent-whatsapp-lead-counts')
            .then((res) => {
                if (res?.data?.status === 1) {
                    setCounts(res.data.counts || {});
                }
            });
    };

    const handleFilter = (e) => {
        e.preventDefault();

        if (Object.keys(filters).some((k) => filters[k])) {
            setLoading(true);
            addFormData('sub-agent-whatsapp-lead-filter', filters)
                .then((res) => {
                    if (res?.data?.status === 1) {
                        setLeads(res.data.leads || []);
                        setPage(1);
                    } else {
                        setLeads([]);
                    }
                })
                .catch(() => toast.error('Filter failed'))
                .finally(() => setLoading(false));
        } else {
            loadLeads();
        }
    };

    const reset = () => {
        if (ref.current) ref.current.reset();
        setFilters({});
        loadLeads();
    };

    const handleInput = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const openHistoryModal = (lead) => {
        setSelectedLead(lead);
        setInteractionHistory([]);
        setHistoryLoading(true);
        setHistoryModal(true);
        getInteractionHistory(lead.id);
    };

    const closeHistoryModal = () => {
        setHistoryModal(false);
        setSelectedLead(null);
        setInteractionHistory([]);
        setHistoryLoading(false);
    };

    const getInteractionHistory = (leadId) => {
        if (!leadId) return;

        setHistoryLoading(true);

        addFormData('sub-agent-whatsapp-lead-history', {
            whatsapp_lead_id: leadId
        })
            .then((res) => {
                if (res?.data?.status === 1) {
                    setInteractionHistory(res.data.history || []);
                } else {
                    setInteractionHistory([]);
                }
            })
            .catch(() => {
                setInteractionHistory([]);
                toast.error('Failed to load history');
            })
            .finally(() => setHistoryLoading(false));
    };
    const openRemarksModal = (lead) => {
        setSelectedLead(lead);
        setRemarks(lead.remarks || '');
        setRemarksModal(true);
    };

    const closeRemarksModal = () => {
        setRemarksModal(false);
        setSelectedLead(null);
        setRemarks('');
    };

    const saveRemarks = () => {
        if (!remarks) {
            toast.error('Remarks cannot be empty!');
            return;
        }

        addFormData('sub-agent-update-remarks', {
            lead_id: selectedLead.id,
            remarks: remarks
        })
            .then((res) => {
                if (res?.data?.status === 1) {
                    toast.success('Remarks updated successfully');
                    setLeads(prevLeads =>
                        prevLeads.map(lead =>
                            lead.id === selectedLead.id
                                ? { ...lead, remarks: remarks }
                                : lead
                        )
                    );
                    setRemarks('');
                    setRemarksModal(false);
                } else {
                    toast.error('Failed to update remarks');
                }
            })
            .catch(() => toast.error('Failed to update remarks'));
    };

    const csvCols = [
        { id: 'lead_date', displayName: 'Date' },
        { id: 'lead_time', displayName: 'Time' },
        { id: 'phone_number', displayName: 'Phone' },
        { id: 'customer_name', displayName: 'Name' },
        { id: 'product_name', displayName: 'Product' },
        { id: 'landing_url', displayName: 'Landing URL' },
        { id: 'label', displayName: 'Labels' },
        { id: 'lead_status', displayName: 'Status' },
        { id: 'remarks', displayName: 'Remarks' },
        { id: 'last_interaction_type', displayName: 'Last Interaction' },
        { id: 'last_interaction_by_name', displayName: 'Interaction By' },
        { id: 'last_interaction_note', displayName: 'Interaction Note' },
        { id: 'last_interaction_at', displayName: 'Interaction At' },
    ];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR::WHATSAPP LEADS</title></Helmet>

            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                .wa-page { animation: fadeInUp 0.4s ease; }

                .wa-hero {
                    background: linear-gradient(135deg, #075e54 0%, #128c7e 60%, #25d366 100%);
                    border-radius: 16px; padding: 20px 24px; color: #fff;
                    margin-bottom: 20px; box-shadow: 0 8px 24px rgba(7,94,84,0.25);
                    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
                }

                .wa-stats { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
                .wa-stat-card {
                    flex: 1; min-width: 130px; border-radius: 14px; padding: 14px 18px;
                    background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                    border-left: 4px solid transparent;
                }

                .wa-filter {
                    background: #fff; border-radius: 16px; padding: 18px 20px; margin-bottom: 18px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                }

                .wa-filter label {
                    font-size: 11px; font-weight: 600; color: #888;
                    text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 5px;
                }

                .wa-filter .form-control {
                    border-radius: 10px; border: 1.5px solid #eee; font-size: 13px; padding: 8px 12px;
                }

                .wa-filter .form-control:focus {
                    border-color: #25d366; box-shadow: 0 0 0 3px rgba(37,211,102,0.1); outline: none;
                }

                .wa-table-card {
                    background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    overflow: hidden; margin-bottom: 50px;
                }

                .wa-table-card .table-scroll {
                    overflow-x: auto; -webkit-overflow-scrolling: touch;
                }

                .wa-table-card table {
                    margin: 0; min-width: 1200px;
                }

                .wa-table-card thead th {
                    background: #f8f9fa; font-size: 11px; font-weight: 700; color: #666;
                    text-transform: uppercase; letter-spacing: 0.5px; padding: 13px 12px;
                    border-bottom: 2px solid #eee; white-space: nowrap;
                }

                .wa-table-card tbody tr:hover { background: #f0fff4; }

                .wa-table-card tbody td {
                    font-size: 13px; color: #444; padding: 11px 12px; vertical-align: middle; white-space: nowrap;
                }

                .round-pagination .page-item.actives .page-link {
                    background: #25d366 !important; border-color: #25d366 !important; color: #fff !important;
                    border-radius: 8px !important;
                }

                .round-pagination .page-item .page-link {
                    background: #fff; border: 1.5px solid #eee; color: #555; border-radius: 8px !important;
                    margin: 0 2px; font-size: 12px; font-weight: 600; padding: 5px 12px; box-shadow: none !important;
                }

                .round-pagination .page-item .page-link:hover {
                    background: #25d366 !important; color: #fff !important;
                }

                .wa-history-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15,23,42,.6);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                }

                .wa-history-modal {
                    width: 100%;
                    max-width: 760px;
                    background: #fff;
                    border-radius: 18px;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0,0,0,.25);
                }

                .wa-history-header {
                    padding: 16px 18px;
                    background: linear-gradient(135deg,#0f172a 0%,#1e293b 100%);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .wa-history-close {
                    background: rgba(255,255,255,.1);
                    color: #fff;
                    border: none;
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    cursor: pointer;
                    font-size: 18px;
                }

                .wa-history-body {
                    padding: 18px;
                    max-height: 500px;
                    overflow: auto;
                    background: #fffdf5;
                }

                .wa-history-item {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px;
                    margin-bottom: 10px;
                }
                .remarks-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }

                .remarks-modal {
                    background: #fff;
                    width: 400px;
                    border-radius: 10px;
                    padding: 20px;
                    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
                }

                .remarks-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .remarks-close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    color: #c62828;
                    cursor: pointer;
                }

                .remarks-modal-body textarea {
                    width: 100%;
                    height: 120px;
                    padding: 10px;
                    border-radius: 8px;
                    border: 1px solid #ccc;
                    font-size: 14px;
                }

                .remarks-modal-body button {
                    width: 100%;
                    padding: 10px;
                    background-color: #c62828;
                    color: #fff;
                    font-weight: bold;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                }

                .remarks-modal-body button:hover {
                    background-color: #b71c1c;
                }
            `}</style>

            <ContentWrapper>
                <div className="wa-page">

                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav>
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                                <li className="breadcrumb-item">WhatsApp Leads</li>
                            </ol>
                        </nav>
                    </div>

                    <div className="wa-hero">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{
                                width: 52,
                                height: 52,
                                borderRadius: 14,
                                background: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 26
                            }}>
                                <FaWhatsapp />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 1 }}>
                                    WhatsApp Leads
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800 }}>{leads.length} Total</div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Today</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.today || 0}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>No Product</div>
                                <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.no_product || 0}</div>
                            </div>
                        </div>
                    </div>

                    <div className="wa-stats">
                        {[
                            { label: 'Total Leads', value: counts.total || 0, color: '#075e54', border: '#075e54' },
                            { label: 'Today', value: counts.today || 0, color: '#128c7e', border: '#25d366' },
                            { label: 'No Product', value: counts.no_product || 0, color: '#e65100', border: '#ff9800' },
                        ].map((s, i) => (
                            <div key={i} className="wa-stat-card" style={{ borderLeftColor: s.border }}>
                                <div style={{
                                    fontSize: 11,
                                    color: '#888',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {s.label}
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, marginTop: 4 }}>
                                    {s.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="wa-filter">
                        <form ref={ref} onSubmit={handleFilter}>
                            <div className="row g-2 align-items-end">
                                <div className="col-6 col-lg-2">
                                    <label>Search Name / Mobile</label>
                                    <input
                                        name="search"
                                        className="form-control"
                                        type="text"
                                        placeholder="Name or mobile"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-6 col-lg-2">
                                    <label>Product Name</label>
                                    <input
                                        name="product"
                                        className="form-control"
                                        type="text"
                                        placeholder="Product name"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-6 col-lg-2">
                                    <label>Label</label>
                                    <input
                                        name="label"
                                        className="form-control"
                                        type="text"
                                        placeholder="Label"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-6 col-lg-2">
                                    <label>Lead Date</label>
                                    <input
                                        name="lead_date"
                                        className="form-control"
                                        type="date"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-6 col-lg-2">
                                    <label>Date From</label>
                                    <input
                                        name="date_from"
                                        className="form-control"
                                        type="date"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-6 col-lg-2">
                                    <label>Date To</label>
                                    <input
                                        name="date_to"
                                        className="form-control"
                                        type="date"
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="col-12">
                                    <div className="d-flex gap-2 flex-wrap" style={{ paddingTop: 8 }}>
                                        <button
                                            type="submit"
                                            className="btn btn-sm"
                                            style={{
                                                background: '#25d366',
                                                color: '#fff',
                                                borderRadius: 8,
                                                fontWeight: 600,
                                                fontSize: 12,
                                                padding: '7px 18px'
                                            }}
                                        >
                                            <FaFilter style={{ marginRight: 4, fontSize: 10 }} /> Filter
                                        </button>

                                        <button
                                            type="button"
                                            onClick={reset}
                                            className="btn btn-sm"
                                            style={{
                                                background: '#fff3cd',
                                                color: '#856404',
                                                borderRadius: 8,
                                                fontWeight: 600,
                                                fontSize: 12,
                                                padding: '7px 14px'
                                            }}
                                        >
                                            <FaRedo style={{ marginRight: 4, fontSize: 11 }} /> Reset
                                        </button>

                                        <CsvDownloader filename="WhatsApp_Leads" extension=".csv" datas={leads} columns={csvCols}>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                style={{
                                                    background: '#e3f2fd',
                                                    color: '#1565c0',
                                                    borderRadius: 8,
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    padding: '7px 14px'
                                                }}
                                            >
                                                <CiExport style={{ marginRight: 4, fontSize: 14 }} /> Export
                                            </button>
                                        </CsvDownloader>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="wa-table-card">
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 20px',
                            background: '#f8fffe',
                            borderBottom: '1px solid #e8f5e9'
                        }}>
                            <span style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>
                                Showing <strong>{records.length}</strong> of <strong>{leads.length}</strong> leads
                            </span>
                            <span style={{ fontSize: 13, color: '#666' }}>
                                Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong>
                            </span>
                        </div>

                        <div className="table-scroll">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Phone Number</th>
                                        <th>Name</th>
                                        <th>Product Name</th>
                                        <th>Landing Page</th>
                                        <th>Labels</th>
                                        <th>Lead Status</th>
                                        <th>Remarks</th>
                                        <th>Last Interaction</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? (
                                                <tr>
                                                    <td colSpan={10} className="text-center py-5" style={{ color: '#bbb' }}>
                                                        <FaWhatsapp style={{ fontSize: 32, color: '#eee', display: 'block', margin: '0 auto 8px' }} />
                                                        No leads found
                                                    </td>
                                                </tr>
                                            )
                                            : records.map((lead, i) => (
                                                <tr key={lead.id || i}>
                                                    <td style={{ color: '#aaa', fontSize: 12 }}>
                                                        {(currentPage - 1) * perPage + i + 1}
                                                    </td>

                                                    <td style={{ fontSize: 12, fontWeight: 600, color: '#075e54' }}>
                                                        {lead.lead_date}
                                                    </td>

                                                    <td style={{ fontSize: 12, color: '#888' }}>
                                                        {lead.lead_time}
                                                    </td>

                                                    <td>
                                                        <a
                                                            href={`https://wa.me/91${lead.phone_number}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                color: '#25d366',
                                                                fontWeight: 700,
                                                                fontSize: 13,
                                                                textDecoration: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 4
                                                            }}
                                                        >
                                                            <FaWhatsapp style={{ fontSize: 14 }} /> {lead.phone_number}
                                                        </a>
                                                    </td>

                                                    <td style={{ fontWeight: 500 }}>
                                                        {lead.customer_name}
                                                    </td>

                                                    <td>
                                                        {lead.product_name ? (
                                                            <span style={{
                                                                background: '#e8f5e9',
                                                                color: '#075e54',
                                                                padding: '2px 8px',
                                                                borderRadius: 20,
                                                                fontSize: 11,
                                                                fontWeight: 600
                                                            }}>
                                                                {lead.product_name}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                                                        )}
                                                    </td>

                                                    <td>
                                                        {lead.landing_url ? (
                                                            <a
                                                                href={lead.landing_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: '#1565c0',
                                                                    maxWidth: 200,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: 'inline-block',
                                                                    whiteSpace: 'nowrap',
                                                                    verticalAlign: 'middle',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                }}
                                                                title={lead.landing_url}
                                                            >
                                                                {lead.landing_url}
                                                            </a>
                                                        ) : '—'}
                                                    </td>

                                                    <td>
                                                        {lead.label ? (
                                                            <span style={{
                                                                background: '#fff3e0',
                                                                color: '#e65100',
                                                                padding: '2px 8px',
                                                                borderRadius: 20,
                                                                fontSize: 11,
                                                                fontWeight: 600
                                                            }}>
                                                                {lead.label}
                                                            </span>
                                                        ) : '—'}
                                                    </td>

                                                    <td>
                                                        <span style={{
                                                            background: lead.lead_status === 'new_customer' ? '#e3f2fd' : '#e8f5e9',
                                                            color: lead.lead_status === 'new_customer' ? '#1565c0' : '#0e7c52',
                                                            padding: '2px 8px',
                                                            borderRadius: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600
                                                        }}>
                                                            {lead.lead_status || 'new_customer'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {lead.remarks ? (
                                                            <>
                                                                {lead.remarks}
                                                                <FaEdit
                                                                    style={{ cursor: 'pointer', color: '#4CAF50', marginLeft: "5px" }}
                                                                    onClick={() => openRemarksModal(lead)}
                                                                />
                                                            </>

                                                        ) : (
                                                            <FaPlus
                                                                style={{ cursor: 'pointer', color: '#2196F3' }}
                                                                onClick={() => openRemarksModal(lead)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{ minWidth: 250, whiteSpace: 'normal', cursor: 'pointer' }}
                                                        onClick={() => openHistoryModal(lead)}
                                                    >
                                                        {lead.last_interaction_type ? (
                                                            <div style={{
                                                                background: '#f8fafc',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: 12,
                                                                padding: '10px 12px'
                                                            }}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    marginBottom: 6,
                                                                    gap: 8
                                                                }}>
                                                                    <div style={{ fontWeight: 700 }}>
                                                                        {lead.last_interaction_type}
                                                                    </div>
                                                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                                                        {lead.last_interaction_by_name || '—'}({lead.last_interaction_emp_id || '—'})
                                                                    </div>
                                                                </div>

                                                                <div style={{ fontSize: 12 }}>
                                                                    {lead.last_interaction_note || '—'}
                                                                </div>

                                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                                                                    {lead.last_interaction_at
                                                                        ? moment(lead.last_interaction_at, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YYYY hh:mm A')
                                                                        : '—'}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ color: '#94a3b8' }}>No interaction yet</div>
                                                        )}
                                                    </td>

                                                </tr>
                                            ))
                                    }
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a
                                            href="#"
                                            className="page-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) setPage(currentPage - 1);
                                            }}
                                        >
                                            ← Prev
                                        </a>
                                    </li>

                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a
                                                href="#"
                                                className="page-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setPage(n);
                                                }}
                                            >
                                                {n}
                                            </a>
                                        </li>
                                    ))}

                                    <li className="page-item">
                                        <a
                                            href="#"
                                            className="page-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < npage) setPage(currentPage + 1);
                                            }}
                                        >
                                            Next →
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>

                    {historyModal && (
                        <div className="wa-history-overlay">
                            <div className="wa-history-modal">
                                <div className="wa-history-header">
                                    <div>
                                        Interaction History — {selectedLead?.customer_name || selectedLead?.phone_number}
                                    </div>
                                    <button className="wa-history-close" onClick={closeHistoryModal}>×</button>
                                </div>

                                <div className="wa-history-body">
                                    {historyLoading ? (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>
                                            Loading...
                                        </div>
                                    ) : interactionHistory.length > 0 ? (
                                        interactionHistory.map((item) => (
                                            <div key={item.id} className="wa-history-item">
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: 8,
                                                    marginBottom: 6
                                                }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                                        {item.interaction_type}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: '#64748b' }}>
                                                        {item.agent_name || '—'}({item.agent_emp_id || '—'})
                                                    </div>
                                                </div>

                                                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.45 }}>
                                                    {item.interaction_note || '—'}
                                                </div>

                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                                                    {item.created_at
                                                        ? moment(item.created_at, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YYYY hh:mm A')
                                                        : '—'}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>
                                            No interaction yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Remarks Modal */}
                    {remarksModal && (
                        <div className="remarks-modal-overlay">
                            <div className="remarks-modal">
                                <div className="remarks-modal-header">
                                    <span>{remarks ? `Edit Remarks for ${selectedLead?.customer_name}` : `Add Remarks for ${selectedLead?.customer_name}`}</span>
                                    <button className="remarks-close-btn" onClick={closeRemarksModal}>×</button>
                                </div>

                                <div className="remarks-modal-body">
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Enter remarks..."
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ccc',
                                            marginTop: '10px',
                                        }}
                                    />
                                    <button
                                        onClick={saveRemarks}
                                        style={{ marginTop: '12px', backgroundColor: remarks ? '#FF6347' : '#4CAF50' }}
                                    >
                                        {remarks ? 'Update Remarks' : 'Add Remarks'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(WhatsappLeads);